import { useCallback, useEffect, useState } from "react";
import axios from "../api/axios";

const fieldLabels = {
  name: "Full name",
  mobile: "Mobile number",
  email: "Email address",
  dateOfBirth: "Date of birth",
  nomineeName: "Nominee name",
  nomineeRelation: "Nominee relation",
  nomineePhone: "Nominee phone",
  state: "State",
  district: "District",
  tahsil: "Tahsil",
  postOffice: "Post office",
  pinCode: "PIN code",
  address: "Address",
};

const formatValue = (field, value) => {
  if (!value) return "—";
  if (field === "dateOfBirth") return new Date(value).toLocaleDateString();
  return String(value);
};

const AdminProfileUpdateRequests = ({ onChanged }) => {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadRequests = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get("/profile-updates/admin");
      setRequests(data.requests || []);
      if (onChanged) onChanged();
    } catch (loadError) {
      setError(loadError.response?.data?.message || "Unable to load profile update requests.");
    } finally {
      setLoading(false);
    }
  }, [onChanged]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const reviewRequest = async (request, action) => {
    setError("");
    setNotice("");
    const reason = (reasons[request._id] || "").trim();
    if (action === "reject" && !reason) {
      setError("Enter a rejection reason before rejecting a request.");
      return;
    }

    setActingOn(request._id);
    try {
      await axios.post(`/profile-updates/admin/${request._id}/${action}`, action === "reject" ? { reason } : {});
      setNotice(`Profile update request ${action === "approve" ? "approved" : "rejected"}.`);
      await loadRequests();
    } catch (reviewError) {
      setError(reviewError.response?.data?.message || `Unable to ${action} this request.`);
    } finally {
      setActingOn("");
    }
  };

  const filteredRequests = requests.filter((request) => statusFilter === "all" || request.status === statusFilter);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Profile Update Requests</h2>
          <p className="mt-1 text-sm text-slate-500">Review changes before they are applied to user accounts.</p>
        </div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All statuses</option>
          </select>
          <button type="button" onClick={loadRequests} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            Refresh
          </button>
        </div>
      </div>

      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{notice}</div>}
      {loading ? (
        <p className="py-8 text-center text-sm text-slate-500">Loading requests…</p>
      ) : filteredRequests.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">No profile update requests found.</div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <article key={request._id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-3">
                <div>
                  <h3 className="font-bold text-slate-800">{request.userId?.name || "Deleted user"}</h3>
                  <p className="text-sm text-slate-500">
                    ID: {request.userId?.uniqueId || "—"} · Mobile: {request.userId?.mobile || "—"} · Email: {request.userId?.email || "—"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Submitted {new Date(request.createdAt).toLocaleString()}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${
                  request.status === "approved" ? "bg-green-100 text-green-800" :
                    request.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                }`}>
                  {request.status}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {Object.entries(request.requestedChanges || {}).filter(([field]) => field !== "_id").map(([field, value]) => (
                  <div key={field} className="rounded-lg bg-slate-50 p-3 text-sm">
                    <p className="mb-1 font-semibold text-slate-700">{fieldLabels[field] || field}</p>
                    <p className="break-words text-slate-500">Current: {formatValue(field, request.previousValues?.[field])}</p>
                    <p className="break-words font-medium text-slate-900">Requested: {formatValue(field, value)}</p>
                  </div>
                ))}
              </div>

              {request.status === "pending" ? (
                <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-end">
                  <label className="flex-1 text-sm font-medium text-slate-700">
                    Rejection reason (required to reject)
                    <textarea
                      value={reasons[request._id] || ""}
                      onChange={(event) => setReasons((current) => ({ ...current, [request._id]: event.target.value }))}
                      maxLength={1000}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => reviewRequest(request, "approve")}
                      disabled={actingOn === request._id}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {actingOn === request._id ? "Saving…" : "Approve"}
                    </button>
                    <button
                      type="button"
                      onClick={() => reviewRequest(request, "reject")}
                      disabled={actingOn === request._id}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">
                  {request.status === "rejected" && <p><strong>Reason:</strong> {request.rejectionReason}</p>}
                  {request.reviewedAt && <p className="mt-1 text-xs text-slate-500">Reviewed {new Date(request.reviewedAt).toLocaleString()}</p>}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProfileUpdateRequests;
